<template>
  <div>
    <breadcrumb :items="breadcrumbs"></breadcrumb>

    <v-container fluid>
      <div class="page-title mb-6">
        <h2>{{ $t('goals.updateGoal') }}</h2>
        <v-spacer></v-spacer>

        <v-btn
          outlined
          class="mr-2"
          right
          height="44"
          width="116"
          depressed
          color="primary"
          :to="{ name: 'GoalDetails', params: {
            id: $route.params.id
          }}"
        >
          {{ $t('cancel') }}
        </v-btn>
        <v-btn
          right
          height="44"
          width="116"
          color="primary"
          @click="updateGoal"
        >
        {{ $t('save') }}
        </v-btn>
      </div>
      <v-card>
        <v-form ref="addUser" lazy-validation>
          <v-card-text class="black--text">
            <v-row>
              <v-col cols="3">
                <label for="name"
                  >{{ $t('goals.goalName') }}<span class="red--text">*</span></label
                >
                <v-text-field
                  placeholder="$t('goals.goalNameHere')"
                  v-model="goalData.goalName"
                  :rules="goalRules.goalName"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="3">
                <label for="Farm/Zone"
                  >{{ $t('goals.farmZone') }}<span class="red--text">*</span></label
                >
                <v-text-field
                  v-model="farmSegmentsPlaceholder"
                  :rules="goalRules.farmId"
                  id="dialog-link"
                  outlined
                  readonly
                  dense
                  @click="showFarmSegmentsDialog = true"
                ></v-text-field>
              </v-col>
              <v-col cols="3">
                <label for="Goal Subject"
                  >{{ $t('goals.goalSubject') }}<span class="red--text">*</span></label
                >
                <v-text-field
                  v-model="goalSubjectPlaceholder"
                  :rules="goalRules.goalSubject"
                  outlined
                  dense
                  id="dialog-link"
                  @click="showGoalSubjectDialog = true"
                ></v-text-field>
              </v-col>
              <v-col cols="3">
                <label for="email">{{ $t('goals.primaryGoal') }}</label>
                <v-select
                  item-text="primary_goal"
                  item-value="id"
                  :items="primaryGoals"
                  v-model="goalData.primaryGoal"
                  outlined
                  dense
                  :placeholder="$t('goals.selectGoalList')"
                ></v-select>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="3">
                <label for="Base Value">
                  {{ $t('goals.baseValue') }}
                  <span class="float-right">{{ primaryGoalUnit }}</span>
                </label>
                <v-text-field
                  v-model="goalData.baseValue"
                  :placeholder="$t('goals.typeCurrentValue')"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="3">
                <label for="Target Value">
                  {{ $t('goals.targetValue') }}
                  <span class="float-right">{{ primaryGoalUnit }}</span>
                </label>
                <v-text-field
                  v-model="goalData.targetValue"
                  :placeholder="$t('goals.typeYourTargetValue')"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="3">
                <label for="Target Deadline">
                  {{ $t('goals.targetDeadline') }}
                </label>
                <v-menu
                  ref="targetDeadlineMenu"
                  v-model="targetDeadlineMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="goalData.targetDeadline"
                      persistent-hint
                      prepend-inner-icon="mdi-calendar"
                      v-bind="attrs"
                      @blur="date = parseDate(goalData.targetDeadline)"
                      v-on="on"
                      v-on:click:prepend-inner="targetDeadlineMenu=true"
                      outlined
                      dense
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="goalData.targetDeadline"
                    no-title
                    @input="targetDeadlineMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="3">
                <label for="Reminder/Schedule">{{ $t('goals.reminderSchedule') }}</label>
                <v-text-field
                  v-model="goalData.reminder"
                  outlined
                  dense
                  :placeholder="$t('goals.reminderScheduleMsg')"
                ></v-text-field>
              </v-col>
              <v-col cols="3">
                <label for="Goal History"
                  >{{ $t('goals.goalHistory') }} <img src="/img/goal-history.svg"
                /></label>
                <v-text-field
                  v-model="goalHistoryPlaceholder"
                  outlined
                  dense
                  id="dialog-link"
                  @click="showGoalHistoryDialog = true"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="9">
                <label for="Comments/Condition/Expectation"
                  >{{ $t('goals.commentsConditionExpectation') }}</label
                >
                <v-text-field
                  v-model="goalData.comment"
                  :placeholder="$t('goals.goalNameHere')"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card-text>
        </v-form>
      </v-card>
    </v-container>

    <goal-subject-dialog
      v-if="showGoalSubjectDialog"
      :dialogI="showGoalSubjectDialog"
      @closeChooseSubject="showGoalSubjectDialog = false"
      @emitGoalSubjects="onGoalSubjectEmit"
      :goalSubjectsProp="goalSubjects"
    />

    <farm-segments-dialog
      v-if="showFarmSegmentsDialog"
      :dialogI="showFarmSegmentsDialog"
      @closeDialog="showFarmSegmentsDialog = false"
      @emitFarmSegments="onFarmSegmentsEmit"
      :farmSegmentsProp="farmSegments"
    />

    <goal-history-dialog
      v-if="showGoalHistoryDialog"
      :dialogI="showGoalHistoryDialog"
      @emitGoalHistory="onGoalHistoryEmit"
      :goalHistoryProp="goalHistories"
      @closeGoalHistoryDialog="showGoalHistoryDialog = false"
    />

    <registration-success-dialog
      v-if="showRegistrationSuccessDialog"
      :dialogI="showRegistrationSuccessDialog"
      @closeSuccessDialog="resetForm"
    />
  </div>
</template>

<script>
import loadingMixin from "@/mixins/LoadingMixin"
import GoalSubjectDialog from "@/views/livestock_goals/GoalSubjectDialog"
import GoalHistoryDialog from "@/views/livestock_goals/GoalHistoryDialog"
import FarmSegmentsDialog from "@/views/livestock_goals/FarmSegmentsDialog"
import RegistrationSuccessDialog from "@/views/livestock_goals/RegistrationSuccessDialog"
import gql from 'graphql-tag'
import { mapGetters } from "vuex"
import { goalRules } from "@/validations/AddGoalsRules.js"
import generalMixin from "@/mixins/GeneralMixin"

export default {
  name: 'UpdateGoal',
  async mounted() {
    document.title = this.$t('goals.updateGoal')
    this.startLoading()
    setTimeout(() => {
      this.stopLoading()
    }, 1000)
  },
  async created() {
    await this.$apollo.queries.goal.refetch()
    await this.getGoalData()
  },
  components: {
    "goal-subject-dialog": GoalSubjectDialog,
    "goal-history-dialog": GoalHistoryDialog,
    "registration-success-dialog": RegistrationSuccessDialog,
    "farm-segments-dialog": FarmSegmentsDialog
  },
  data() {
    return {
      loading: true,
      farmIdToNameObj: {},
      breadcrumbs: [
        {
          text: this.$t("dashbaord"),
          disabled: false,
          to: "/",
        },
        {
          text: this.$t("liveStockGoals"),
          disabled: false,
          to: "/livestock-goals",
        },
        {
          text: this.$t('goals.updateGoal'),
          disabled: true,
          to: "/",
        },
      ],
      showGoalSubjectDialog: false,
      showGoalHistoryDialog: false,
      showRegistrationSuccessDialog: false,
      showFarmSegmentsDialog: false,
      goalSubjects: [],
      goalHistories: [],
      farmSegments: {
        farm: [],
        segments: []
      },
      goalData: {
        goalName: null,
        userId: null,
        farmId: null,
        segmentId: [],
        primaryGoal: null,
     
        baseValue: null,
        targetValue: null,
        targetDeadline: null,
        reminder: null,
        comment: null
      },
      goalRules,
      date: (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10),
      targetDeadlineMenu: false
    }
  },
  methods: {
    updateGoal () {
      if (!this.$refs.addUser.validate()) return
      this.startLoading()
      this.goalData.id = Number(this.$route.params.id)
      this.goalData.userId = this.getUser.id
      this.goalData.organization = this.getUser.user_organization.id
      this.goalData.farmId = Number(this.farmSegments.farm)
      this.goalData.baseValue = Number(this.goalData.baseValue)
      this.goalData.targetValue = Number(this.goalData.targetValue)
      this.goalData.segmentId = this.farmSegments.segments.map(item => Number(item.id))
      this.goalData.goalSubjects = this.goalSubjects.map(item => ({
        ...item,
        animalCategory: parseInt(item.animalCategory)
      }))
      this.goalData.goalHistories = this.goalHistories

      // console.log(this.goalData)
      this.$apollo.mutate({
        // Query
        mutation: gql`mutation ($data: UpdateGoalInput!) {
          updateGoal (updateGoalInput: $data) {
            id
          }
        }`,
        // Parameters
        variables: {
          data: this.goalData,
        },
      }).then(async res => {
        // console.log(res)
        if (res.data.updateGoal && res.data.updateGoal.id) {
          this.stopLoading()
          this.$router.push({name: 'GoalDetails', params: { id: this.$route.params.id }})
        }
      }).catch(async error => {
        console.log(error)
        this.stopLoading()
      })
    },
    onGoalSubjectEmit (value) {
      this.goalSubjects = value
    },
    onGoalHistoryEmit (value) {
      this.goalHistories = value
    },
    onFarmSegmentsEmit(value) {
      this.farmIdToNameObj = value.farmIdToNameObj
      this.farmSegments = value.selected
    },
    parseDate (date) {
      if (!date) return null

      const [month, day, year] = date.split('/')
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    },
    resetForm () {
      this.showRegistrationSuccessDialog = false
      this.goalData = {
        goalName: '',
        userId: '',
        farmId: '',
        segmentId: [],
        primaryGoal: null,
       
        baseValue: '',
        targetValue: '',
        targetDeadline: '',
        reminder: '',
        comment: ''
      }
    },
    getGoalData () {
      this.goalData = {
        goalName: this.goal.goal_name,
        userId: this.goal.user_id,
        primaryGoal: this.goal.primaryGoalData.id,
     
        baseValue: this.goal.base_value,
        targetValue: this.goal.target_value,
        targetDeadline: this.formatDate(this.goal.target_deadline),
        reminder: this.goal.reminder,
        comment: this.goal.comment
      }
      this.farmSegments = {
        farm: this.goal.userFarmData.length > 0 && this.goal.userFarmData.map(item => item.farm_id)[0].id,
        segments: this.goal.userSegmentData.length > 0 && this.goal.userSegmentData.map(item => item.segment_id.geofenceName)
      }
      this.goalSubjects = this.goal.goalSubjectMapData.map(item => ({
        animalBreed: item.animalTypeData && item.animalTypeData.id,
        animalCategory: item.animal_category && item.animal_category.id,
        animalGroup: item.animalGroupData && item.animalGroupData.id,
        animalType: item.animalTypeData && item.animalTypeData.id,
        goalSubjectId: item.goalSubjectData.id
      }))
      this.goalHistories = this.goal.goalHistory.map(item => ({
        baseValueHistory: item.base_value,
        finalValueHistory: item.final_value,
        goalEndDate: this.formatDate(item.goal_end_date)

      }))
    }
  },
  computed: {
    farmSegmentsPlaceholder () {
      let placeholder = 'Choose farm or zone'
      if (this.farmSegments.farm.length > 0) {
        placeholder = this.farmIdToNameObj[this.farmSegments.farm] || this.goal.userFarmData[0].farm_id.farmName
      } else if (this.farmSegments.segments.length > 0) {
        placeholder = `${this.farmSegments.segments.length} segments selected`
      }
      return placeholder
    },
    goalSubjectPlaceholder () {
      return this.goalSubjects.length > 0 ?  `${this.goalSubjects.length} subject/s added`: 'Set goal for?'
    },
    goalHistoryPlaceholder () {
      return this.goalHistories.length > 0 ?  `${this.goalHistories.length} history added`: 'Fill the history of similar goal'
    },
    ...mapGetters(['getUser']),
    primaryGoalUnit () {
      const goal = this.primaryGoals.filter(item => item.id === this.goalData.primaryGoal)
      if (goal.length > 0) {
        return goal[0].primary_goal_unit
      }
      return null
    }
  },
  mixins: [loadingMixin, generalMixin],
  apollo: {
    primaryGoals: gql`query {
      primaryGoals {
        id,
        primary_goal,
        primary_goal_unit
      }
    }`,
   
    goal: {
      query () {
        return gql`query goal ($id: Int!) {
          goal (id: $id) {
          id
          goal_name
          user_id {
            id
            firstName
            lastName
          }
          userFarmData {
            id
            farm_id {
              id
              farmName
              lat
            }
          }
          userSegmentData{
            id
            segment_id {
              id,
              geofenceName
            }
          }
          primaryGoalData{
            id
            primary_goal
          }
          base_value
          target_value
          target_deadline,
          reminder,
          comment,
          goalHistory {
            id
            goal_id,
            base_value,
            final_value,
            goal_end_date
          }
          goalSubjectMapData {
            id
            goal_id
            goalSubjectData {
              id
              subject
            }
            animalTypeData {
              id
              animal_type
            }
            animalBreedData {
              id
              animal_breed
            }
            animalGroupData {
              id
              name
            }
            animal_category {
              id
              name
            }
          }
        }
        }`
      },
      variables() {
        return {
          id: Number(this.$route.params.id),
        }
      },
      update: data => data.goal
    }
  }
}
</script>

<style lang="scss" scoped>
.border {
  border: 2px solid;
  border-color: #dae1ec;
}
.v-tab {
  text-transform: none !important;
}
::v-deep #dialog-link{
  cursor: pointer;
  color: #002287;
  text-decoration: underline;
}
</style>
